<template>
    <base-dialog :loading="loading" :value="dialog" :width="480"
        :title="`${editMode ? $t('common.edit') : $t('common.create')} ${$t('common.folder')}`" @input="onInput"
        @close="hide">
        <div v-if="!confirmingDelete" class="folder-fields">
            <kimsa-text-field @change="name = $event" :value="name" :label="$t('common.name')"
                :placeholder="$t('common.folder_name_placeholder')" force-value dense no-elevation />
            <kimsa-upload-file v-if="!noImage" :label="$t('common.image')" relative-path="images/folders"
                mime-types="image/*" @change="file = $event; deletedImg = false" :preview-img="previewImg" ref="img"
                height="150" @delete="deletedImg = true" />
        </div>
        <p v-else class="folder-confirm">{{ $t('common.confirm_delete_folder') }}</p>

        <template #footer>
            <div class="folder-footer">
                <template v-if="confirmingDelete">
                    <v-btn depressed tile class="folder-btn folder-btn--cancel" @click="confirmingDelete = false">
                        {{ $t('common.cancel') }}
                    </v-btn>
                    <v-btn depressed tile color="error" class="folder-btn" :loading="loadingDelete"
                        @click="deleteFolder">
                        {{ $t('common.delete') }}
                    </v-btn>
                </template>
                <template v-else>
                    <v-btn v-if="editMode" depressed tile color="error" class="folder-btn"
                        @click="confirmingDelete = true">
                        {{ $t('common.delete') }}
                    </v-btn>
                    <v-btn depressed tile color="primary" class="folder-btn black--text" :disabled="disabled"
                        :loading="loading" @click="save">
                        {{ editMode ? $t('common.edit') : $t('common.create') }}
                    </v-btn>
                </template>
            </div>
        </template>
    </base-dialog>
</template>

<script>
import { db, Timestamp, deleteStorageFile } from "@/utils/Firebase";
import KimsaTextField from "@/components/common/inputs/KimsaTextField";
import KimsaUploadFile from "@/components/common/inputs/KimsaUploadFile";
import BaseDialog from "@/components/common/BaseDialog";

export default {
    name: "AdminFoldersDialog",
    components: { KimsaUploadFile, KimsaTextField, BaseDialog },
    props: {
        type: {
            type: String,
            default: null,
        },
        coachId: {
            type: String,
            default: null,
        },
        folder: {
            type: Object,
            default: null,
        },
        noImage: {
            type: Boolean,
            default: false,
        },
        isSecondDialog: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            dialog: false,
            loading: false,
            name: '',
            file: null,
            loadingDelete: false,
            deletedImg: false,
            confirmingDelete: false,
        }
    },
    computed: {
        disabled(vm = this) {
            return !vm.name || vm.name.length < 3 || vm.loadingDelete
        },
        previewImg(vm = this) {
            if (vm.folder?.img && !vm.deletedImg) return vm.folder?.img || null
            return vm.file?.img || null
        },
        editMode(vm = this) {
            return !!vm.folder
        },
    },
    methods: {
        show() {
            const vm = this
            vm.dialog = true
        },
        hide() {
            const vm = this
            vm.dialog = false
            vm.reset()
            vm.$emit('hide')
        },
        onInput(val) {
            if (!val) this.hide()
        },
        reset() {
            const vm = this
            vm.name = ''
            vm.file = null
            vm.deletedImg = false
            vm.confirmingDelete = false
        },
        async deleteFolder() {
            const vm = this
            await vm.withLoading(async () => {
                const imgSavePath = vm.folder.imgSavePath
                await db.collection('folders').doc(vm.folder.id).delete()
                if (imgSavePath) await deleteStorageFile(imgSavePath)

                let refs = await db.collection(vm.type).where('folder', '==', vm.folder.id).get()
                if (refs.docs.length > 0) {
                    for (let ref of refs.docs) {
                        await db.collection(vm.type).doc(ref.id).update({ folder: null })
                    }
                }

                vm.hide()
                vm.toast(vm.$t('common.folder_deleted'))
            }, 'loadingDelete')
        },
        async save() {
            const vm = this
            await vm.withLoading(async () => {
                let payload = { name: vm.name }
                let img = null
                if (vm.file) {
                    img = await vm.$refs.img.upload()
                    payload.img = img?.tempUrl || ''
                    payload.imgSavePath = img?.savePath || ''
                }

                if (vm.editMode) {
                    const oldImgSavePath = vm.folder.imgSavePath
                    const removingImg = vm.deletedImg && !img
                    if (removingImg) {
                        payload.img = null
                        payload.imgSavePath = null
                    }
                    await db.collection('folders').doc(vm.folder.id).update(payload)
                    const newImgSavePath = img?.savePath || null
                    if (oldImgSavePath && (removingImg || (newImgSavePath && newImgSavePath !== oldImgSavePath))) {
                        await deleteStorageFile(oldImgSavePath)
                    }
                } else {
                    payload.type = vm.type
                    payload.coachId = vm.coachId
                    payload.createdAt = Timestamp.now()
                    await db.collection('folders').add(payload)
                }

                vm.hide()
                vm.editMode ?
                    vm.toast(vm.$t('common.folder_updated')) :
                    vm.toast(vm.$t('common.folder_created'))
            })
        },
    },
    watch: {
        folder(val) {
            if (val?.name) this.name = val.name
        }
    }
}
</script>

<style scoped>
.folder-fields {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.folder-confirm {
    font-size: 14px;
    color: var(--v-txtSecondary-base);
    line-height: 1.6;
    margin: 0;
}

.folder-footer {
    display: flex;
}

.folder-btn {
    flex: 1;
    height: 52px !important;
    border-radius: 0 !important;
    font-weight: 700;
}

.folder-btn--cancel {
    flex: 0.65;
    color: var(--v-txtSecondary-base) !important;
    background: var(--v-surface2-base) !important;
}
</style>